import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  English: {
    translation: {
      // Header / layout
      'app.name': 'Ecom Factory',
      'notifications.title': 'Notifications',
      'notifications.emptyTitle': 'No notifications yet',
      'notifications.emptySubtitle': "You\'ll see updates when phases complete",

      // Dashboard
      'dashboard.pipelineOverview': 'Pipeline Overview',
      'dashboard.allProducts': 'All Products',
      'dashboard.noProductsTitle': 'No products found',
      'dashboard.noProductsAll': 'Start by sniping your first product',
      'dashboard.noProductsStage': 'No products in the {{stage}} stage',

      // Snipe modal
      'snipe.title': 'Snipe New Product',
      'snipe.description': 'Paste any product URL to start the research pipeline',
      'snipe.urlLabel': 'Product URL',
      'snipe.supportedPlatforms':
        'Supports Amazon, Etsy, Alibaba, Walmart and any other product URL (marked as custom)',
      'snipe.detectedPlatform': 'Detected Platform',
      'snipe.outputLanguage': 'Output Language',
      'snipe.outputLanguageHelp':
        'This controls the language of marketing angles, product descriptions and other generated content.',
      'snipe.cancel': 'Cancel',
      'snipe.submit': 'Snipe Product',
      'snipe.submitting': 'Submitting...',

      // Languages
      'language.english': 'English',
      'language.french': 'French',
      'language.italian': 'Italian',
      'language.german': 'German',
      'language.select': 'Select language…',

      // Sidebar
      'sidebar.home': 'Home',
      'sidebar.productResearch': 'Product Research',
      'sidebar.winningProducts': 'Winning Products',
      'sidebar.creativeAds': 'Creative Ads',
      'sidebar.shopify': 'Shopify',
      'sidebar.collapse': 'Collapse',
      'sidebar.language': 'Language',
      'sidebar.allowSound': 'Allow Sound',
      'sidebar.signOut': 'Sign Out',

      // Product Research page
      'research.title': 'Product Research',
      'research.subtitle': '{{products}} products • {{reviews}} with reviews',
      'research.emptyTitle': 'No products yet',
      'research.emptySubtitle': 'Products will appear here once sniped',
      'research.reviewsBadge': 'Reviews ✓',

      // Winning Products page
      'winning.title': 'Winning Products',
      'winning.subtitle': '{{count}} products',
      'winning.emptyTitle': 'No winning products yet',
      'winning.emptySubtitle': 'Products will appear here once they reach the winning stage',

      // Ads page
      'ads.title': 'Creative Ads',
      'ads.subtitle': '{{ugc}} UGC packages • {{staticCount}} static ad campaigns',
      'ads.filterPlaceholder': 'Filter by product',
      'ads.allProducts': 'All Products',
      'ads.ugcTab': 'UGC Videos ({{count}})',
      'ads.staticTab': 'Static Ads ({{count}})',
      'ads.ugc.emptyTitle': 'No UGC ads found',
      'ads.ugc.emptySubtitle': 'UGC ads will appear here once generated',
      'ads.static.emptyTitle': 'No static ads found',
      'ads.static.emptySubtitle': 'Static ads will appear here once generated',
      'ads.fullVideosLabel': 'Full Videos',
      'ads.shortsLabel': 'Shorts',
      'ads.videosReady': '{{count}} videos ready',
      'ads.imagesCount': '{{count}} images',
      'ads.adSets': '8 ad sets',

      // Confirm product details (human in the loop)
      'confirm.title': 'Confirm Product Details',
      'confirm.subtitle': 'Review and confirm winning details for product {{ecomId}}.',
      'confirm.errorLoad': 'Failed to load product details.',
      'confirm.errorSubmit': 'Failed to submit confirmation. Please try again.',
      'confirm.success': 'Product details submitted for approval.',
      'confirm.notFound': 'Product not found.',
      'confirm.backHome': 'Back to Dashboard',
      'confirm.nameLabel': 'Product name',
      'confirm.nameHelp': 'You can adjust the product name if needed. Other fields below are read-only.',
      'confirm.nameIdeasHint': 'Need inspiration? Let AI suggest copyright-free names.',
      'confirm.generateNames': 'Generate name ideas',
      'confirm.generatingNames': 'Generating...',
      'confirm.noMarketingData': 'Marketing data is not available for this product yet.',
      'confirm.nameServiceNotConfigured': 'Name ideas service is not configured.',
      'confirm.noNamesReturned': 'No name suggestions were returned.',
      'confirm.namesGenerated': 'Name ideas generated.',
      'confirm.errorGenerateNames': 'Failed to generate name ideas. Please try again.',
      'confirm.suggestionsTitle': 'Suggested names',
      'confirm.notesLabel': 'Additional notes',
      'confirm.notesPlaceholder': 'Optional notes or context for your team (will be sent with the confirmation).',
      'confirm.descriptionLabel': 'Description',
      'confirm.descriptionTitle': 'Product Description',
      'confirm.featuresLabel': 'Features',
      'confirm.featuresTitle': 'Product Features',
      'confirm.howToUseLabel': 'How to Use',
      'confirm.howToUseTitle': 'How to Use',
      'confirm.marketingContext': 'Marketing context (read-only)',
      'confirm.marketingAngle': 'Marketing Angle',
      'confirm.competitiveEdge': 'Competitive Edge',
      'confirm.personas': 'Customer Personas',
      'confirm.confirmButton': 'Confirm details',
      'confirm.submitting': 'Submitting...',
    },
  },
  French: {
    translation: {
      'app.name': 'Ecom Factory',
      'dashboard.pipelineOverview': 'Vue d’ensemble du pipeline',
      'sidebar.home': 'Accueil',
      'sidebar.productResearch': 'Recherche produit',
      'sidebar.winningProducts': 'Produits gagnants',
      'sidebar.creativeAds': 'Publicités créatives',
      'sidebar.shopify': 'Shopify',
    },
  },
  Italian: {
    translation: {
      'app.name': 'Ecom Factory',
      'dashboard.pipelineOverview': 'Panoramica della pipeline',
      'sidebar.home': 'Home',
      'sidebar.productResearch': 'Ricerca prodotto',
      'sidebar.winningProducts': 'Prodotti vincenti',
      'sidebar.creativeAds': 'Annunci creativi',
      'sidebar.shopify': 'Shopify',
    },
  },
  German: {
    translation: {
      'app.name': 'Ecom Factory',
      'dashboard.pipelineOverview': 'Pipeline-Übersicht',
      'sidebar.home': 'Startseite',
      'sidebar.productResearch': 'Produktrecherche',
      'sidebar.winningProducts': 'Gewinnerprodukte',
      'sidebar.creativeAds': 'Kreative Anzeigen',
      'sidebar.shopify': 'Shopify',
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'English',
    supportedLngs: ['English', 'French', 'Italian', 'German'],
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;

